import React from 'react';
import './Receipt.css';

function beatLabel(beatId) {
  return beatId ? `Moment ${beatId.slice(0, 8)}` : 'Moment';
}

/**
 * S4 — the receipt (Unit 4 build spec §E1, design brief §2-S4). Reachable
 * after any COMPLETE-shaped pipeline result. `claims` come straight from
 * checking_status.receipt_claims() (PR A) — never re-derived here, so the
 * honesty rules stay centralized in one backend function.
 *
 * Cost: `actualCost` is pipeline.py's checks["actual_cost"] (punch-list
 * §2.3) — real per-call usage priced at live rates. Shown when present;
 * falls back to the fare-card estimate, honestly labeled as an estimate,
 * for receipts from before this landed or where the pipeline recorded no
 * calls (edit_scene's receipts never have it — no LLM call to price).
 * Never fabricated: a model whose live price wasn't found is named, not
 * silently folded into the total (brief §3's honesty rule).
 */
export default function Receipt({
  claims, findings, advisoryFindings, beatAccount, harvestedFacts,
  modelName, estimate, actualCost, editHistoryNote, overruledFindings, attempts,
  onConfirmFact, onRejectFact, onDone, factActionBusyId,
}) {
  const beats = beatAccount?.beats || [];
  const delivered = beats.filter((b) => b.delivered);
  const notDelivered = beats.filter((b) => !b.delivered);
  const hasActualCost = actualCost && actualCost.calls && actualCost.calls.length > 0;

  return (
    <div className="er-receipt">
      <div className="er-receipt__inner">
        <div className="er-receipt__head">
          <span className="er-receipt__eyebrow">Receipt</span>
          <h2 className="er-receipt__title">What this draft delivered.</h2>
        </div>

        <section className="er-receipt__section">
          <h3 className="er-receipt__section-title">What was checked</h3>
          <ul className="er-receipt__claims">
            {Object.entries(claims || {}).map(([category, line]) => (
              <li key={category} className="er-receipt__claim">{line}</li>
            ))}
          </ul>
        </section>

        {(findings.length > 0 || advisoryFindings.length > 0 || (overruledFindings || []).length > 0) && (
          <section className="er-receipt__section">
            <h3 className="er-receipt__section-title">Findings on this draft</h3>
            <ul className="er-receipt__finding-list">
              {findings.map((f) => (
                <li key={f.id} className="er-receipt__finding">{f.explanation}</li>
              ))}
              {advisoryFindings.map((f) => (
                <li key={f.id} className="er-receipt__finding er-receipt__finding--advisory">
                  <span className="er-receipt__advisory-tag">Advisory</span> {f.explanation}
                </li>
              ))}
              {(overruledFindings || []).map((f) => (
                <li key={f.id} className="er-receipt__finding er-receipt__finding--overruled">
                  <span className="er-receipt__overruled-tag">Overruled by you</span> {f.explanation}
                </li>
              ))}
            </ul>
          </section>
        )}

        {harvestedFacts && harvestedFacts.length > 0 && (
          <section className="er-receipt__section">
            <h3 className="er-receipt__section-title">New details this draft invented</h3>
            <p className="er-receipt__section-sub">Confirm to make it story law, or reject to leave it as this draft&rsquo;s alone.</p>
            <ul className="er-receipt__harvest-list">
              {harvestedFacts.map((f) => (
                <li key={f.fact_id} className="er-receipt__harvest">
                  <p className="er-receipt__harvest-text">{f.text}</p>
                  {f.state === 'proposed' ? (
                    <div className="er-receipt__harvest-actions">
                      <button
                        type="button" className="er-receipt__harvest-act"
                        disabled={factActionBusyId === f.fact_id}
                        onClick={() => onConfirmFact(f.fact_id)}
                      >
                        Confirm
                      </button>
                      <button
                        type="button" className="er-receipt__harvest-act er-receipt__harvest-act--reject"
                        disabled={factActionBusyId === f.fact_id}
                        onClick={() => onRejectFact(f.fact_id)}
                      >
                        Reject
                      </button>
                    </div>
                  ) : (
                    <span className="er-receipt__harvest-state">{f.state}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {beats.length > 0 && (
          <section className="er-receipt__section">
            <h3 className="er-receipt__section-title">Moments covered</h3>
            <ul className="er-receipt__beat-list">
              {delivered.map((b) => (
                <li key={b.beat_id} className="er-receipt__beat">
                  <span className="er-receipt__beat-label">{beatLabel(b.beat_id)}</span>
                  <blockquote className="er-receipt__beat-quote">&ldquo;{b.quote}&rdquo;</blockquote>
                </li>
              ))}
              {notDelivered.map((b) => (
                <li key={b.beat_id} className="er-receipt__beat er-receipt__beat--missing">
                  <span className="er-receipt__beat-label">{beatLabel(b.beat_id)}</span>
                  <span className="er-receipt__beat-missing-note">not credited</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="er-receipt__section er-receipt__section--quiet">
          <div className="er-receipt__quiet-row">
            <span>Model{modelName ? `: ${modelName}` : ''}</span>
            {hasActualCost ? (
              <span>Cost: ${actualCost.total_cost_usd.toFixed(2)}</span>
            ) : (
              estimate && !estimate.missing_pricing && <span>Estimated cost: ${estimate.estimate_usd.toFixed(2)}</span>
            )}
            <span>{attempts} attempt{attempts === 1 ? '' : 's'}</span>
          </div>
          {hasActualCost && actualCost.missing_pricing.length > 0 && (
            <div className="er-receipt__quiet-row er-receipt__cost-gap">
              Pricing unavailable for {actualCost.missing_pricing.join(', ')} — cost above excludes those calls.
            </div>
          )}
          {editHistoryNote && <div className="er-receipt__quiet-row">{editHistoryNote}</div>}
        </section>

        <div className="er-receipt__foot">
          <button type="button" className="er-receipt__done" onClick={onDone}>Back to the outline</button>
        </div>
      </div>
    </div>
  );
}
